/**
 * Reusable "Enabled Languages" checkbox picker — used in Organization
 * settings, Survey editor, and Card Settings (Review Shield + Landing).
 *
 * Pure-presentational. Parent owns the state.
 */
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const OPTIONS = [
    { code: 'en', labelKey: 'languages.en', fallback: 'English', flag: '🇬🇧' },
    { code: 'fr', labelKey: 'languages.fr', fallback: 'Français', flag: '🇫🇷' },
    { code: 'es', labelKey: 'languages.es', fallback: 'Español', flag: '🇪🇸' },
];

export function EnabledLanguagesPicker({
    value,                    // string[] | null (null = inherit)
    onChange,                 // (next: string[] | null) => void
    allowInherit = false,     // show "Inherit from organization" radio
    inheritedFrom = null,     // resolved inherited list (for label)
    className = '',
    disabled = false,
    testIdPrefix = 'enabled-languages',
}) {
    const { t } = useTranslation();
    const inheriting = allowInherit && (value === null || value === undefined);
    const current = Array.isArray(value) ? value : (inheritedFrom || OPTIONS.map(o => o.code));

    const toggle = (code) => {
        if (disabled || inheriting) return;
        const set = new Set(current);
        if (set.has(code)) set.delete(code); else set.add(code);
        // Preserve canonical order en→fr→es
        const next = OPTIONS.map(o => o.code).filter(c => set.has(c));
        if (next.length === 0) return;  // enforce at least one
        onChange(next);
    };

    return (
        <div className={`space-y-3 ${className}`} data-testid={`${testIdPrefix}-picker`}>
            <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-fuchsia-600" />
                <span className="text-sm font-medium">{t('languages.enabledLanguages', 'Enabled Languages')}</span>
            </div>

            {allowInherit && (
                <div className="flex gap-4 text-sm pl-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            checked={inheriting}
                            onChange={() => onChange(null)}
                            disabled={disabled}
                            className="h-3.5 w-3.5 accent-fuchsia-600"
                            data-testid={`${testIdPrefix}-inherit-radio`}
                        />
                        <span>{t('languages.inheritFromOrg', 'Inherit from organization')}
                            {inheritedFrom && inheritedFrom.length > 0 && (
                                <span className="text-muted-foreground text-xs ml-1.5">
                                    ({inheritedFrom.join(' · ')})
                                </span>
                            )}
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            checked={!inheriting}
                            onChange={() => onChange(Array.isArray(value) && value.length ? value : (inheritedFrom || ['en','fr','es']))}
                            disabled={disabled}
                            className="h-3.5 w-3.5 accent-fuchsia-600"
                            data-testid={`${testIdPrefix}-override-radio`}
                        />
                        <span>{t('languages.overrideForThis', 'Override for this')}</span>
                    </label>
                </div>
            )}

            <div className="flex flex-wrap gap-3 pl-1">
                {OPTIONS.map(opt => {
                    const active = current.includes(opt.code);
                    const effectiveDisabled = disabled || inheriting;
                    return (
                        <label
                            key={opt.code}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm select-none transition-colors ${
                                active
                                    ? 'bg-fuchsia-50 dark:bg-fuchsia-900/30 border-fuchsia-300 dark:border-fuchsia-700 text-fuchsia-900 dark:text-fuchsia-200'
                                    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                            } ${effectiveDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-fuchsia-400'}`}
                            data-testid={`${testIdPrefix}-option-${opt.code}`}
                        >
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggle(opt.code)}
                                disabled={effectiveDisabled}
                                className="h-3.5 w-3.5 accent-fuchsia-600"
                                data-testid={`${testIdPrefix}-checkbox-${opt.code}`}
                            />
                            <span>{opt.flag}</span>
                            <span className="font-medium">{t(opt.labelKey, opt.fallback)}</span>
                        </label>
                    );
                })}
            </div>
            <p className="text-xs text-muted-foreground pl-1">
                {t('languages.helper', 'Customers see a welcome screen only when more than one language is enabled.')}
            </p>
        </div>
    );
}

export default EnabledLanguagesPicker;
